import * as CubiqTS from "shared/cubiq-model-multi-asset";
import { MappedCurrencyPairCalendarImpl } from "../mapped/MappedCurrencyPairCalendarImpl";
import { LimitOrder } from "./LimitOrder";

// OMS-30890 Per Keith D, "-" is reserved for empty numerical fields.
import { EMPTY_TEXT_VALUE } from "shared/cubiq-services/model/BaseTableDefinitionFactory";
import { CurrencyPairCalendarLookupService } from "../../lookup/model/impl/CurrencyPairCalendarLookupService";
import { COLUMN_VALUE_DATE } from "../OrdersTableDefinitionFactory.constants";
import { BmarkRiskData } from "shared/cubiq-services/model/benchmarks/BmarkRiskData.model";
import { BENCHMARK_TERMINAL_STATE } from "../benchmarks/BmarkTerminalState.const";

import { LoggerService } from "@bfx/logger";
const logger = LoggerService.for(__dirname);

export const PENDING_FWD_STATE: Set<CubiqTS.OrderState> = new Set([
  CubiqTS.OrderState.PENDING_FWDS,
  CubiqTS.OrderState.EXPIRY_PENDING_FWDS,
  CubiqTS.OrderState.CANCEL_PENDING_FWDS,
]);

type ValueDateParams = { value: CubiqTS.Moment; data: { settleTenor?: CubiqTS.Tenor } };

export class ValueDateHelper {
  protected localeString: string = "DD-MMM-YY";

  constructor(protected currencyPairCalendarLookupService: CurrencyPairCalendarLookupService) {}

  /**
   * CubiqTS.Moment in UTC (FIXME: FXBFXOM-9972)
   */
  public getMoment(order: LimitOrder): CubiqTS.Moment {
    let tenor: CubiqTS.Tenor = order.settleTenor;
    let adjustedTenor: CubiqTS.Tenor = order.adjustedSettleTenor;
    let vd: CubiqTS.Moment = order[COLUMN_VALUE_DATE];
    let vdSort: number = 0;
    let vdSortOffset: number = tenor;

    if (
      (order.orderType == CubiqTS.OrderType.BENCHMARK || PENDING_FWD_STATE.has(order.orderState)) &&
      order.settleDate != null
    ) {
      return order.settleDate;
    }

    try {
      if (
        order.orderState === CubiqTS.OrderState.FILLED ||
        BENCHMARK_TERMINAL_STATE.has(order.orderState)
      ) {
        if (ValueDateHelper.isCanceledOrExpiredNotPartFilledAlgo(order)) {
          return null;
        }

        if (order.settleDate != null) {
          vd = order.settleDate;
        }

        if (vd) {
          vdSort = vd.valueOf() + vdSortOffset;
        }

        order.valueDateSort = vdSort;

        return vd;
      }
    } catch (e) {
      logger.out.warn(`${this.constructor.name}: Failed to convert order value date ${e}`);
    }

    if (tenor) {
      let ccyPairKey = `${order.baseCode}${order.nonBaseCode}`;
      let calendar = this.currencyPairCalendarLookupService.getItem(
        ccyPairKey,
      ) as MappedCurrencyPairCalendarImpl;
      let valueDate: CubiqTS.StandardDateImpl = null;
      if (calendar) {
        if (CubiqTS.Product.SPT === order.product || CubiqTS.Product.FWD === order.product) {
          if (adjustedTenor && calendar.tenorToValueDateMap.has(adjustedTenor)) {
            let tenorToValueDate = calendar.tenorToValueDateMap.get(adjustedTenor);
            valueDate = tenorToValueDate.valueDate;
          } else if (calendar.tenorToValueDateMap.has(tenor)) {
            let tenorToValueDate = calendar.tenorToValueDateMap.get(tenor);
            valueDate = tenorToValueDate.valueDate;
          }
        } else if (
          CubiqTS.Product.NDF === order.product &&
          calendar.tenorToValueAndFixingDateMap.has(tenor)
        ) {
          let valueDateAndFixing = calendar.tenorToValueAndFixingDateMap.get(tenor);
          valueDate = valueDateAndFixing.valueDate;
        }
        // milliseconds on a StandardDate (day boundary).
        // add Tenor to this value for correct sort
        if (valueDate) {
          vd = CubiqTS.moment.tz([valueDate.year, valueDate.month - 1, valueDate.day], "UTC");
        }
      }
    } else {
      vdSortOffset = CubiqTS.Tenor.ALL;
    }
    if (vd) {
      vdSort = vd.valueOf() + vdSortOffset;
    }
    order.valueDateSort = vdSort;
    return vd;
  }

  formatValueDate(date: any /* Moment - but cannot import*/, tenor: CubiqTS.Tenor): string {
    try {
      if (date) {
        let dateStr: string = CubiqTS.moment(date).utc().format(this.localeString);
        if (tenor != undefined) {
          return `${this.getTenorStr(tenor)} (${dateStr})`;
        }
        return dateStr;
      } else if (tenor != undefined) {
        return `${this.getTenorStr(tenor)} (-)`;
      }
    } catch (e) {
      logger.out.warn(`${this.constructor.name}: Failed to format order value date ${e}`);
    }

    return EMPTY_TEXT_VALUE;
  }

  /**
   * Format a Benchmark risk total value date for use in the pair summary tooltip
   * @param date
   * @param tenor
   * @returns
   */
  public formatBMRiskValueDate(
    date: any /* Moment - but cannot import*/,
    tenor: CubiqTS.Tenor,
  ): string {
    try {
      let dateStr: string;
      let tenorStr: string;

      if (tenor != null && tenor != CubiqTS.Tenor.EMPTY && tenor != CubiqTS.Tenor.ALL) {
        tenorStr = this.getTenorStr(tenor);
        if (tenorStr === "<UNKNOWN>") {
          tenorStr = null;
        }
      }
      if (!tenorStr) {
        tenorStr = "BKN";
      }

      if (date) {
        dateStr = CubiqTS.moment(date).utc().format(this.localeString);
      } else {
        dateStr = "(-)";
      }
      return `${tenorStr} (${dateStr})`;
    } catch (e) {
      logger.out.warn(`${this.constructor.name}: Failed to format order value date ${e}`);
    }

    return EMPTY_TEXT_VALUE;
  }

  /**
   * Given a Risk Data that is known to be a SWAP, return the appropriate formatted pair of value dates
   * @param risk
   * @returns
   */
  public formatSwapValueDate(
    nearTenor: CubiqTS.Tenor,
    nearValueDate: any,
    farTenor: CubiqTS.Tenor,
    farValueDate: any,
  ): string {
    let nearTenorStr = nearTenor != null ? this.getTenorStr(nearTenor) : "BRKN";
    let farTenorStr = farTenor != null ? this.getTenorStr(farTenor) : "BRKN";
    let nearDateStr = nearValueDate ? nearValueDate.utc().format(this.localeString) : "-";
    let farDateStr = farValueDate ? farValueDate.utc().format(this.localeString) : "-";

    return `${nearTenorStr}/${farTenorStr} (${nearDateStr}/${farDateStr})`;
  }

  /**
   * Get the string representation of a tenor and Value date using a order's settleTenor and settleDate properties.
   * Usea full currency pair calendars
   * @param order
   * @returns String of form Tenor (Value Date ) e.g. 'SP (25 DEC 2201')
   */
  formatTenorValue(order: LimitOrder): string {
    const valueDateMoment = this.getMoment(order);
    return this.formatValueDate(valueDateMoment, order.settleTenor);
  }

  public getTenorStr(tenor: CubiqTS.Tenor): string {
    if (typeof tenor === "number") {
      switch (tenor) {
        case CubiqTS.Tenor.TDY:
          return "TDY";
        case CubiqTS.Tenor.TOM:
          return "TOM";
        case CubiqTS.Tenor.SPOT:
          return "SP";
        case CubiqTS.Tenor.W1:
          return "1W";
        case CubiqTS.Tenor.W2:
          return "2W";
        case CubiqTS.Tenor.W3:
          return "3W";
        case CubiqTS.Tenor.M1:
          return "1M";
        case CubiqTS.Tenor.M2:
          return "2M";
        case CubiqTS.Tenor.M3:
          return "3M";
        case CubiqTS.Tenor.M4:
          return "4M";
        case CubiqTS.Tenor.M5:
          return "5M";
        case CubiqTS.Tenor.M6:
          return "6M";
        case CubiqTS.Tenor.M7:
          return "7M";
        case CubiqTS.Tenor.M8:
          return "8M";
        case CubiqTS.Tenor.M9:
          return "9M";
        case CubiqTS.Tenor.M10:
          return "10M";
        case CubiqTS.Tenor.M11:
          return "11M";
        case CubiqTS.Tenor.Y1:
          return "1Y";
        case CubiqTS.Tenor.M13:
          return "13M";
        case CubiqTS.Tenor.M14:
          return "14M";
        case CubiqTS.Tenor.M15:
          return "15M";
        case CubiqTS.Tenor.M16:
          return "16M";
        case CubiqTS.Tenor.M17:
          return "17M";
        case CubiqTS.Tenor.M18:
          return "18M";
        case CubiqTS.Tenor.M19:
          return "19M";
        case CubiqTS.Tenor.M20:
          return "20M";
        case CubiqTS.Tenor.M21:
          return "21M";
        case CubiqTS.Tenor.M22:
          return "22M";
        case CubiqTS.Tenor.M23:
          return "23M";
        case CubiqTS.Tenor.Y2:
          return "2Y";
        case CubiqTS.Tenor.Y3:
          return "3Y";
        case CubiqTS.Tenor.Y4:
          return "4Y";
        case CubiqTS.Tenor.Y5:
          return "5Y";
        case CubiqTS.Tenor.Y6:
          return "6Y";
        case CubiqTS.Tenor.Y7:
          return "7Y";
        case CubiqTS.Tenor.Y8:
          return "8Y";
        case CubiqTS.Tenor.Y9:
          return "9Y";
        case CubiqTS.Tenor.Y10:
          return "10Y";
        case CubiqTS.Tenor.SN:
          return "SN";
        case CubiqTS.Tenor.ALL:
          return "All";
      }
      return CubiqTS.Tenor[tenor];
    }
    return "<UNKNOWN>";
  }

  /**
   * If algo is cancelled or expired but not part filled, than
   * we need to hide the value date (some fix channels populate it on server)
   * @param order
   */
  static isCanceledOrExpiredNotPartFilledAlgo(order: CubiqTS.OrderData): boolean {
    let oType = order.orderType;
    let state = order.orderState;
    let execQt = order.executedQty;

    let isAlgo = oType === CubiqTS.OrderType.ALGO || oType === CubiqTS.OrderType.BMALGO;
    let isCE = state === CubiqTS.OrderState.CANCELLED || state === CubiqTS.OrderState.EXPIRED;

    return isAlgo && isCE && (execQt == null || (execQt.isZero && execQt.isZero()));
  }
}
